import type { ThemeChoices } from "$/lib/types/General.types";
import type { KeyValueObject } from "$/lib/types/Graphs.types";
import {
  CONTAINER_HEIGHT_PERCENTAGE,
  generateGraphDateRangeFromData,
  generateXAxisTicks,
  generateYAxisTicks,
  handleGenerateYearReferences,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { cn, getMinMax } from "$/lib/utils/functions/misc.functions";
import { numberFormatter } from "$/lib/utils/functions/util.functions";
import type { ReactNode } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { customColors } from "tailwind.config";
import { useScreen } from "usehooks-ts";
import Flexbox from "../../common/Flexbox";
import type { GraphFilterItemsProps } from "../GraphFilter/GraphFilterItem";
import CustomTooltip from "../LineGraph/CustomTooltip";

type Props = {
  data: KeyValueObject[];
  xAxisDataKey: string;
  areaChartDataKey?: string;
  filtersComponent?: ReactNode;
  unit?: string;
  unit2?: string;
  colors: Record<string, string>;
  keys: string[];
  graphVisibility?: GraphFilterItemsProps[];
  theme: ThemeChoices;
  areaChartColor?: string;
  withGradientEffect?: boolean;
  yAxisLabel1?: string;
  labelPosition1?: string;
  heightPercent?: number;
  yAxisTicksNumber?: number;
  connectNulls?: boolean;
  leftMargin?: number;
  rightMargin?: number;
  hasCustomXAxisDomain?: boolean;
  formatStr?: string;
  keysToFilterFromDomain?: string[];
  title?: string;
  displayNames?: Record<string, string>;
  displayUnits?: Record<string, string>;
  secondYAxis?: {
    type: "AREA" | "LINE";
    dataKey: string;
    color: string;
    label?: string;
    labelPosition?: string;
    unit?: string;
  };
};

const gradientOffset = (data: unknown[], key: string) => {
  // @ts-expect-error - typescript error from the side of the library
  const dataMax = Math.max(...data.map((i) => i[key]));
  // @ts-expect-error - typescript error from the side of the library
  const dataMin = Math.min(...data.map((i) => i[key]));

  if (dataMax <= 0) {
    return 0;
  }
  if (dataMin >= 0) {
    return 1;
  }

  return dataMax / (dataMax - dataMin);
};

const ComposedLineGraph = ({
  data,
  xAxisDataKey,
  filtersComponent,
  colors,
  areaChartDataKey,
  unit = " €/MWh",
  unit2 = " ",
  keys,
  graphVisibility,
  theme,
  areaChartColor = "#C8C9C980",
  withGradientEffect = false,
  yAxisLabel1,
  labelPosition1 = "insideBottomLeft",
  heightPercent,
  yAxisTicksNumber,
  connectNulls = false,
  leftMargin = -20,
  rightMargin = 20,
  formatStr = "dd/MM/yy",
  hasCustomXAxisDomain = true,
  keysToFilterFromDomain,
  title,
  displayNames,
  displayUnits,
  secondYAxis,
}: Props) => {
  const { height: screenHeight } = useScreen();

  const graphHeight = useMemo(
    () =>
      heightPercent
        ? Math.round(screenHeight * CONTAINER_HEIGHT_PERCENTAGE) *
          (heightPercent / 100)
        : 500,
    [heightPercent, screenHeight],
  );

  const visibleKeys = useMemo(
    () =>
      graphVisibility
        ? graphVisibility.filter(
            (item) =>
              item.name !== areaChartDataKey &&
              item.name !== secondYAxis?.dataKey,
          )
        : keys.map((key) => ({
            name: key,
            isVisible: true,
          })),
    [graphVisibility, keys, areaChartDataKey, secondYAxis],
  );

  const hideKeys = useMemo(
    () =>
      visibleKeys.reduce<string[]>((acc, item) => {
        if (item.isVisible === false) return [...acc, item.name];

        return acc;
      }, []),
    [visibleKeys],
  );

  const domain = useMemo(() => {
    const minMax = getMinMax(
      data,
      keys.filter((key) => !keysToFilterFromDomain?.includes(key)),
    );

    return [minMax.min.value, minMax.max.value];
  }, [data, keys, keysToFilterFromDomain]);

  const references = useMemo(
    () => handleGenerateYearReferences(data, xAxisDataKey, formatStr),
    [data, formatStr, xAxisDataKey],
  );

  const dateRange = useMemo(
    () =>
      hasCustomXAxisDomain
        ? generateGraphDateRangeFromData(data, formatStr, xAxisDataKey)
        : undefined,
    [data, formatStr, xAxisDataKey, hasCustomXAxisDomain],
  );

  const xDomain = useMemo(
    () =>
      hasCustomXAxisDomain && dateRange && dateRange[0] && dateRange[1]
        ? generateXAxisTicks(dateRange[0], dateRange[1], formatStr)
        : undefined,
    [dateRange, hasCustomXAxisDomain, formatStr],
  );

  const areaVisibility = useMemo(
    () =>
      graphVisibility
        ? graphVisibility.find((item) => item.name === areaChartDataKey)
        : undefined,
    [graphVisibility, areaChartDataKey],
  );

  const secondYAxisVisibility = useMemo(
    () =>
      graphVisibility
        ? graphVisibility.find((item) => item.name === secondYAxis?.dataKey)
        : undefined,
    [graphVisibility, secondYAxis],
  );

  // const notVisibleKeys = (graphVisibility ?? [])
  //   .filter(
  //     (item) =>
  //       item.name !== areaChartDataKey &&
  //       !item.isVisible &&
  //       item.isVisible !== undefined,
  //   )
  //   .map((item) => item.name);

  return (
    <Flexbox fullHeight fullWidth row className="gap-5">
      <Flexbox fullWidth fullHeight className="relative gap-2">
        <Flexbox row fullWidth align="center" justify="between">
          <h2
            className={cn("text-sm font-bold", {
              "text-secondary": theme === "blue",
              "text-forest": theme === "green",
              "text-warning": theme === "orange",
            })}
          >
            {title}
          </h2>
        </Flexbox>

        <ResponsiveContainer width="100%" height="100%" minHeight={100}>
          <ComposedChart
            data={data}
            margin={{
              top: 10,
              right: rightMargin,
              left: leftMargin,
              bottom: 5,
            }}
          >
            {references?.map(({ value, label }) => (
              <ReferenceLine
                key={value}
                x={label}
                stroke={customColors.tertiary.DEFAULT}
                strokeDasharray={5}
              />
            ))}

            <XAxis
              dataKey={xAxisDataKey}
              fontSize={12}
              interval={hasCustomXAxisDomain ? 0 : "preserveStartEnd"}
              tickMargin={5}
              ticks={xDomain}
            />

            <YAxis
              yAxisId={secondYAxis ? 0 : undefined}
              tickSize={2}
              width={85}
              fontWeight={500}
              fontSize={12}
              tickMargin={5}
              unit={unit}
              domain={domain}
              orientation="left"
              tickFormatter={numberFormatter}
              interval={0}
              ticks={
                Array.isArray(domain)
                  ? generateYAxisTicks(
                      Number(domain[0]),
                      Number(domain[1]),
                      yAxisTicksNumber,
                    )
                  : undefined
              }
              label={{
                value: yAxisLabel1,
                angle: -90,
                position: labelPosition1,
              }}
            />
            {secondYAxis && (
              <YAxis
                yAxisId={1}
                tickSize={2}
                dataKey={secondYAxis.dataKey}
                width={85}
                fontWeight={500}
                fontSize={12}
                tickMargin={5}
                unit={secondYAxis.unit ?? unit2}
                orientation="right"
                // domain={domain}
                // mirror
                tickFormatter={numberFormatter}
                // label={{
                //   value: yAxisLabel2,
                //   angle: 90,
                //   position: labelPosition2,
                // }}
                label={(props: { viewBox: { x: number } }) => {
                  return (
                    <text
                      transform={`rotate(90, ${props.viewBox.x + 40}, ${graphHeight / 2 - 10})`}
                      fill="#808080"
                    >
                      <tspan x={props.viewBox.x + 30} y={graphHeight / 2 - 30}>
                        {secondYAxis.label}
                      </tspan>
                    </text>
                  );
                }}
              />
            )}

            <Tooltip
              content={
                <CustomTooltip
                  unit={unit}
                  theme={theme}
                  hideKeys={hideKeys}
                  displayNames={displayNames}
                  displayUnits={displayUnits}
                />
              }
            />

            {withGradientEffect && areaChartDataKey && (
              <defs>
                <linearGradient id={`splitColor`} x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset={gradientOffset(data, areaChartDataKey)}
                    stopColor={areaChartColor}
                    stopOpacity={1}
                  />
                  <stop
                    offset={gradientOffset(data, areaChartDataKey)}
                    stopColor={areaChartColor}
                    stopOpacity={1}
                  />
                </linearGradient>
              </defs>
            )}

            {areaChartDataKey && (
              <Area
                type="monotone"
                dataKey={areaChartDataKey}
                stroke="none"
                fill={withGradientEffect ? `url(#splitColor)` : areaChartColor}
                connectNulls={connectNulls}
                dot={false}
                activeDot={false}
                fillOpacity={areaVisibility?.isVisible !== false ? 1 : 0}
              />
            )}

            {secondYAxis ? (
              secondYAxis.type === "AREA" ? (
                <Area
                  yAxisId={1}
                  dataKey={secondYAxis.dataKey}
                  stroke="none"
                  fill={
                    withGradientEffect ? `url(#splitColor)` : secondYAxis.color
                  }
                  connectNulls={connectNulls}
                  dot={false}
                  activeDot={false}
                  fillOpacity={
                    secondYAxisVisibility?.isVisible !== false ? 1 : 0
                  }
                />
              ) : (
                <Line
                  yAxisId={1}
                  type="monotone"
                  dataKey={secondYAxis.dataKey}
                  stroke={secondYAxis.color || customColors.tertiary.DEFAULT}
                  dot={false}
                  activeDot={false}
                  strokeWidth={
                    secondYAxisVisibility?.isVisible === undefined ||
                    secondYAxisVisibility?.isVisible
                      ? 2
                      : 0
                  }
                  connectNulls={connectNulls}
                />
              )
            ) : (
              <></>
            )}

            {visibleKeys.map((item) => (
              <Line
                key={item.name}
                yAxisId={secondYAxis ? 0 : undefined}
                type="monotone"
                dataKey={item.name}
                stroke={
                  colors && colors[item.name]
                    ? colors[item.name]
                    : customColors.primary.DEFAULT
                }
                dot={false}
                activeDot={false}
                strokeWidth={
                  item.isVisible === undefined || item.isVisible ? 2 : 0
                }
                connectNulls={connectNulls}
              />
            ))}

            {/* {graphVisibility &&
            notVisibleKeys.map((key) => (
              <Line
                yAxisId={withSecondYAxis ? 0 : undefined}
                type="monotone"
                dataKey={key}
                stroke={customColors.tertiary.DEFAULT}
                strokeOpacity={0.5}
                dot={false}
                activeDot={false}
              />
            ))} */}
          </ComposedChart>
        </ResponsiveContainer>
      </Flexbox>

      {filtersComponent}
    </Flexbox>
  );
};

export default ComposedLineGraph;
